// SAT Talep — kullanici acisindan: kendi taleplerini listeler, yeni talep
// olusturmak icin modal acar. Rol bagimsiz sayfa (herkes kullanir).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../providers/I18nProvider";
import {
  satRequestService,
  SAT_CATEGORIES,
  SAT_PRIORITIES,
} from "../services/satRequestService";
import StatusBadge from "../components/satRequest/StatusBadge";
import PriorityBadge from "../components/satRequest/PriorityBadge";
import SatFilterBar from "../components/satRequest/SatFilterBar";
import {
  isClosedStatus,
  isPendingStatus,
  requestTotal,
  useSatListFilters,
  waitingDays,
} from "../components/satRequest/satListUtils";
import { TabBar, EmptyState, countBy } from "../components/ListToolbar";
import PaginationControls from "../components/PaginationControls";
import "../styles/SATRequest.css";

const emptyForm = {
  title: "",
  description: "",
  category: "unit_need",
  priority: "medium",
  item_url: "",
  quantity: 1,
  unit_price: 0,
  needs_it_review: false,
};
const ALLOWED_PROFORMA_MIMES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_PROFORMA_BYTES = 10 * 1024 * 1024;

const formatDate = (d, lang) => {
  if (!d) return "";
  const locale = lang === "tr" ? "tr-TR" : "en-US";
  return new Date(d).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const validateForm = (f, t) => {
  const errors = [];
  const title = String(f.title || "").trim();
  if (title.length < 3 || title.length > 200) errors.push(t("satRequest.validation.titleLen"));
  const description = String(f.description || "").trim();
  if (description.length < 10 || description.length > 5000)
    errors.push(t("satRequest.validation.descriptionLen"));
  const url = String(f.item_url || "").trim();
  if (url && !/^https?:\/\/\S+/i.test(url)) errors.push(t("satRequest.validation.urlInvalid"));
  const qty = Number(f.quantity);
  if (!Number.isFinite(qty) || qty < 1) errors.push(t("satRequest.validation.quantityMin"));
  const price = Number(f.unit_price);
  if (!Number.isFinite(price) || price < 0) errors.push(t("satRequest.validation.unitPriceMin"));
  return errors;
};

const SATRequestsPage = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [proformaFile, setProformaFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [tab, setTab] = useState("all");

  const tabPredicates = useMemo(
    () => ({
      all: () => true,
      open: (r) => isPendingStatus(r.status),
      completed: (r) => r.status === "completed",
      closed: (r) => isClosedStatus(r.status),
    }),
    []
  );
  const tabCounts = useMemo(() => countBy(items, tabPredicates), [items, tabPredicates]);
  const tabItems = useMemo(() => items.filter(tabPredicates[tab]), [items, tabPredicates, tab]);
  const list = useSatListFilters(tabItems, { tabKey: tab, pageSizeKey: "pageSize.satMine" });
  const tabs = ["all", "open", "completed", "closed"].map((key) => ({
    key,
    label: t(`satList.tabs.${key}`),
    count: tabCounts[key],
  }));

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await satRequestService.listMine();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPrice = useMemo(() => {
    const q = Number(form.quantity);
    const p = Number(form.unit_price);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return 0;
    return q * p;
  }, [form.quantity, form.unit_price]);

  const submit = async (e) => {
    e.preventDefault();
    const errors = validateForm(form, t);
    if (proformaFile) {
      if (!ALLOWED_PROFORMA_MIMES.includes(proformaFile.type)) {
        errors.push(t("satRequest.validation.fileType") || "Sadece PDF, JPG, PNG yuklenebilir.");
      } else if (proformaFile.size > MAX_PROFORMA_BYTES) {
        errors.push(t("satRequest.validation.fileSize") || "Dosya 10MB'dan kucuk olmali.");
      }
    }
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors([]);
    setSubmitting(true);
    try {
      await satRequestService.create({
        ...form,
        quantity: Number(form.quantity),
        unit_price: Number(form.unit_price),
        file: proformaFile || undefined,
      });
      setForm(emptyForm);
      setProformaFile(null);
      setShowNew(false);
      await load();
    } catch (err) {
      const msg =
        err.response?.data?.msg ||
        err.response?.data?.errors?.[0]?.msg ||
        err.message;
      setFormErrors([msg]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sat-page">
      <div className="sat-page-header">
        <div>
          <h2>{t("satRequest.title")}</h2>
          <div className="subtitle">{t("satRequest.subtitle")}</div>
        </div>
        <button
          type="button"
          className="sat-btn sat-btn--primary"
          onClick={() => setShowNew(true)}
        >
          + {t("satRequest.newRequest")}
        </button>
      </div>

      {error && (
        <div className="sat-empty sat-empty--error" role="alert">
          {error}{" "}
          <button type="button" className="sat-btn" onClick={load}>
            {t("satList.refresh")}
          </button>
        </div>
      )}
      {loading && <div className="sat-empty">{t("common.loading")}</div>}

      {!loading && !error && items.length === 0 && (
        <EmptyState
          message={t("satRequest.myEmpty")}
          action={
            <button type="button" className="sat-btn sat-btn--primary" onClick={() => setShowNew(true)}>
              + {t("satRequest.newRequest")}
            </button>
          }
        />
      )}

      {!loading && items.length > 0 && (
        <>
          <TabBar tabs={tabs} value={tab} onChange={setTab} ariaLabel={t("satRequest.title")} />
          <SatFilterBar list={list} />

          {list.filtered.length === 0 ? (
            <EmptyState message={t("satList.emptyFiltered")} />
          ) : (
            <>
              <table className="sat-table">
                <thead>
                  <tr>
                    <th>{t("satRequest.fields.code")}</th>
                    <th>{t("satRequest.fields.title")}</th>
                    <th>{t("satRequest.fields.category")}</th>
                    <th>{t("satRequest.fields.priority")}</th>
                    <th>{t("satRequest.fields.quantity")}</th>
                    <th>{t("satRequest.fields.totalPrice")}</th>
                    <th>{t("satRequest.fields.status")}</th>
                    <th>{t("satRequest.fields.createdAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.paged.map((r) => {
                    const total = requestTotal(r);
                    const wait = isPendingStatus(r.status) ? waitingDays(r) : null;
                    const open = () => navigate(`/sat-requests/${r.id}`);
                    return (
                      <tr
                        key={r.id}
                        tabIndex={0}
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") open();
                        }}
                      >
                        <td className="sat-code">{r.request_code || "—"}</td>
                        <td>{r.title}</td>
                        <td>{t(`satRequest.category.${r.category}`)}</td>
                        <td>
                          <PriorityBadge priority={r.priority} />
                        </td>
                        <td>{r.quantity}</td>
                        <td>
                          {total ? `${total.toLocaleString(lang === "tr" ? "tr-TR" : "en-US")} TL` : "—"}
                        </td>
                        <td>
                          <StatusBadge status={r.status} />
                          {wait != null && (
                            <div className={`sat-waiting${wait >= 3 ? " late" : ""}`}>
                              {wait === 0
                                ? t("satList.waitingToday")
                                : `${wait} ${t("satList.waiting")}`}
                            </div>
                          )}
                        </td>
                        <td>{formatDate(r.created_at, lang)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <PaginationControls
                totalItems={list.filtered.length}
                page={list.page}
                pageSize={list.pageSize}
                onPageChange={list.setPage}
                onPageSizeChange={list.setPageSize}
              />
            </>
          )}
        </>
      )}

      {showNew && (
        <div
          role="dialog"
          aria-modal="true"
          className="sat-modal-backdrop"
          onClick={() => !submitting && setShowNew(false)}
        >
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="sat-modal">
            <h3 className="sat-modal__title">{t("satRequest.newRequest")}</h3>

            <div className="sat-form-grid">
              <label className="full">
                {t("satRequest.fields.title")}
                <input
                  type="text"
                  maxLength={200}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </label>
              <label className="full">
                {t("satRequest.fields.description")}
                <textarea
                  maxLength={5000}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("satRequest.fields.category")}
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                >
                  {SAT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`satRequest.category.${c}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("satRequest.fields.priority")}
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                >
                  {SAT_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {t(`satRequest.priority.${p}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="full">
                {t("satRequest.fields.itemUrl")}
                <input
                  type="url"
                  placeholder={t("satRequest.fields.itemUrlPlaceholder")}
                  value={form.item_url}
                  onChange={(e) => setForm({ ...form, item_url: e.target.value })}
                />
                <small className="sat-hint">
                  {t("satRequest.itemUrlOptionalHint")}
                </small>
              </label>
              <label className="full">
                {t("satRequest.proformaFile")}
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(e) => setProformaFile(e.target.files?.[0] || null)}
                />
                <small className="sat-hint">
                  {t("satRequest.proformaFileHint")}
                </small>
              </label>
              <label>
                {t("satRequest.fields.quantity")}
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("satRequest.fields.unitPrice")}
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  required
                />
              </label>
              <div className="full sat-hint sat-hint--lg">
                {t("satRequest.fields.totalPrice")}: <strong>{totalPrice} TL</strong>
              </div>
              <label className="full sat-check">
                <input
                  type="checkbox"
                  checked={form.needs_it_review}
                  onChange={(e) => setForm({ ...form, needs_it_review: e.target.checked })}
                />
                <span>
                  <strong>{t("satRequest.fields.needsItReview")}</strong>
                  <br />
                  <span className="sat-hint">{t("satRequest.fields.needsItReviewHint")}</span>
                </span>
              </label>
            </div>

            {formErrors.length > 0 && (
              <ul className="sat-form-errors" role="alert">
                {formErrors.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )}

            <div className="sat-modal__actions">
              <button
                type="button"
                className="sat-btn"
                onClick={() => setShowNew(false)}
                disabled={submitting}
              >
                {t("common.cancel")}
              </button>
              <button type="submit" className="sat-btn sat-btn--primary" disabled={submitting}>
                {submitting ? t("common.loading") : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SATRequestsPage;
